import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close, ExpandMore } from "@mui/icons-material";

import { CardLayout, MTable } from "../v3";
import { getCompanyActivityTableColumns } from "../../pages/superadmin/constants";
import { ActivityService, UserService } from "../../services";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

const ACTIVITY_ENTITY_TYPES = [
  { label: "Company", value: "company" },
  { label: "Plan", value: "plan" },

  { label: "User", value: "user" },
  { label: "Role", value: "role" },
  // { label: "User Transaction", value: "user_transaction" },

  { label: "Account", value: "account" },
  // { label: "Account Transaction", value: "account_transaction" },

  { label: "Project", value: "project" },
  { label: "Stage", value: "stage" },
  { label: "Task", value: "task" },
  { label: "Store", value: "store" },

  { label: "Client", value: "client" },
  // { label: "Client Receivable", value: "client_receivable" },
  // { label: "Client Receipt", value: "client_receipt" },
  // { label: "Client Invoice", value: "client_invoice" },

  { label: "Vendor", value: "vendor" },
  { label: "Purchase Item", value: "purchase_item" },
  // { label: "Purchase Request", value: "purchase_request" },
  // { label: "Purchase Return", value: "purchase_return" },
  // { label: "Purchase Invoice", value: "purchase_invoice" },
  // { label: "Vendor Payment", value: "vendor_payment" },

  { label: "Subcontractor", value: "subcontractor" },
  // { label: "Contract", value: "contract" },
  // { label: "Subcontractor Payment", value: "subcontractor_payment" },

  { label: "Worker", value: "worker" },
  // { label: "Work", value: "work" },
  // { label: "Worker Payment", value: "worker_payment" },

  { label: "Employee", value: "employee" },

  // { label: "Expense", value: "expense" },
  { label: "Expense Category", value: "expense_category" },

  // { label: "Tool", value: "tool" },

  { label: "Performance Guarantee", value: "performance_guarantee" },
];

const ACTIVITY_ACTIONS = [
  { label: "Create", value: "CREATE" },
  { label: "Update", value: "UPDATE" },
  { label: "Delete", value: "DELETE" },
];

const ACTION_CHIP_COLOR = {
  CREATE: "success",
  UPDATE: "warning",
  DELETE: "error",
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "null";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const ChangesList = ({ changes }) => {
  if (!Array.isArray(changes) || changes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No field-level changes recorded.
      </Typography>
    );
  }
  return (
    <Stack divider={<Divider flexItem />} spacing={1} sx={{ width: "100%" }}>
      {changes.map((change, index) => (
        <Box key={`${change.field}-${index}`} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ color: "#674BF5" }}>
            {change.field}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" component="span" sx={{ color: "#999" }}>
              from
            </Typography>
            <Chip label={formatValue(change.oldValue)} size="small" variant="outlined" sx={{ maxWidth: 280 }} />
            <Typography variant="body2" component="span" sx={{ color: "#999" }}>
              to
            </Typography>
            <Chip label={formatValue(change.newValue)} size="small" color="primary" variant="outlined" sx={{ maxWidth: 280 }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const JsonBlock = ({ label, data }) => {
  if (data === null || data === undefined) return null;
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="subtitle2">{label}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          component="pre"
          sx={{
            margin: 0,
            padding: 1.5,
            backgroundColor: "#FAFAFA",
            borderRadius: 1,
            overflow: "auto",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "#5e5d72",
          }}
        >
          {JSON.stringify(data, null, 2)}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const ActivityDetailModal = ({ open, onClose, row }) => {
  if (!row) return null;
  const actionColor = ACTION_CHIP_COLOR[row.action] || "default";
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="h6" component="span">
            Activity #{row.id}
          </Typography>
          <Chip label={row.action} size="small" color={actionColor} />
          <Chip label={row.entityType} size="small" color="primary" variant="outlined" />
          <Typography variant="body2" color="text.secondary">
            Entity Id: {row.entityId}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Performed By
              </Typography>
              <Typography variant="body2">{row.performedBy || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2">{row.createdAt}</Typography>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Field Changes
            </Typography>
            <ChangesList changes={row.changes} />
          </Box>

          <JsonBlock label="Previous Data" data={row.previousData} />
          <JsonBlock label="New Data" data={row.newData} />
          <JsonBlock label="Metadata" data={row.metadata} />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export const CompanyActivityList = ({ companyId }) => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
    dropdowns,
    setDropdowns,
  } = usePersistedTableState({
    tableId: "company-activities",
    defaults: { dropdowns: { entityType: "", action: "", createdById: "" }, sortModel: [{ field: "createdAt", sort: "desc" }] },
  });
  const { entityType: selectedEntityType, action: selectedAction, createdById: selectedCreatedById } = dropdowns;
  const [users, setUsers] = useState([]);
  const [detailModal, setDetailModal] = useState({ open: false, row: null });

  const fetchUsers = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await UserService.getUsers({ companyId, limit: 1000 });
      const rows = data?.rows || [];
      setUsers(
        rows.map((user) => ({
          label: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username || `User #${user.id}`,
          value: user.id,
        })),
      );
    } catch (error) {
      console.error("CompanyActivityList ~ fetchUsers ~ error:", error);
    }
  }, [companyId]);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data: response } = await ActivityService.getActivityLogs({
        companyId,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        entityType: selectedEntityType,
        action: selectedAction,
        createdById: selectedCreatedById,
      });
      setLogs(response?.rows || []);
      setTotalCount(response?.count || 0);
    } catch (error) {
      toast.error("Failed to fetch activity logs");
      console.error("CompanyActivityList ~ fetchData ~ error:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, paginationState, sortModel, selectedEntityType, selectedAction, selectedCreatedById]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSortChange = (model) => {
    if (model.length === 0) return;
    setSortModel(model);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleView = (row) => {
    setDetailModal({ open: true, row: row || null });
  };

  const closeDetailModal = () => setDetailModal({ open: false, row: null });

  const columnHeaders = getCompanyActivityTableColumns(handleView);

  const rows = logs.map((log, index) => {
    const user = log.createdBy;
    const performedBy = user
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username || `User #${log.createdById}`
      : `User #${log.createdById}`;
    return {
      id: log.id,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      createdAt: log.createdAt ? moment(log.createdAt).format("DD MMM YYYY hh:mm A") : "—",
      entityType: log.entityType,
      entityId: log.entityId,
      action: log.action,
      performedBy,
      changesCount: Array.isArray(log.changes) ? `${log.changes.length} field${log.changes.length === 1 ? "" : "s"}` : "—",
      changes: log.changes,
      previousData: log.previousData,
      newData: log.newData,
      metadata: log.metadata,
      createdById: log.createdById,
    };
  });

  const dropdownFilters = [
    {
      key: "entityType",
      label: "Entity Type",
      options: ACTIVITY_ENTITY_TYPES,
      value: selectedEntityType,
      onChange: (value) => {
        setDropdowns({ entityType: value });
        setPaginationState((prev) => ({ ...prev, page: 0 }));
      },
    },
    {
      key: "action",
      label: "Action",
      options: ACTIVITY_ACTIONS,
      value: selectedAction,
      onChange: (value) => {
        setDropdowns({ action: value });
        setPaginationState((prev) => ({ ...prev, page: 0 }));
      },
    },
    {
      key: "createdById",
      label: "Performed By",
      options: users,
      value: selectedCreatedById,
      onChange: (value) => {
        setDropdowns({ createdById: value });
        setPaginationState((prev) => ({ ...prev, page: 0 }));
      },
    },
  ];

  return (
    <>
      <CardLayout sx={{ margin: "11px" }}>
        <MTable
          dropdownFilters={dropdownFilters}
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount}
          loading={loading}
          autoHeight
          sortModel={sortModel}
          defaultSortModel={[{ field: "createdAt", sort: "desc" }]}
          onSortChange={handleSortChange}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
        />
      </CardLayout>

      <ActivityDetailModal open={detailModal.open} onClose={closeDetailModal} row={detailModal.row} />
    </>
  );
};
